import { getApp, getApps, initializeApp } from "firebase/app";
import type { User } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import FrontendLoggerFactory from "utils/logging/FrontendLoggerFactory";

import { createAdminAuth } from "./firebase/createAdminAuth";
import {
  connectLocalAdminFirebaseEmulator,
  readLocalAdminFirebaseEmulator,
} from "./firebase/localFirebaseEmulator";

const logger = FrontendLoggerFactory.getLogger().withComponent("firebase");

const resolveBrowserContext = () => {
  if (typeof window === "undefined") {
    return {
      currentPath: null,
      documentReadyState: null,
      hostname: null,
      origin: null,
      currentHost: null,
      isHttpsOrigin: null,
      visibilityState: null,
    };
  }

  return {
    currentPath: window.location.pathname,
    documentReadyState: document.readyState,
    hostname: window.location.hostname,
    origin: window.location.origin,
    currentHost: window.location.host,
    isHttpsOrigin: window.location.protocol === "https:",
    visibilityState: document.visibilityState,
  };
};

const redactTail = (value: string | undefined, tailLength: number) => {
  if (!value) {
    return null;
  }

  if (value.length <= tailLength) {
    return value;
  }

  return value.slice(-tailLength);
};

const getProviderIds = (user: User | null): string[] => {
  const providerData = user?.providerData ?? [];
  if (providerData.length === 0) {
    return [];
  }

  return providerData
    .map((entry) => entry.providerId)
    .filter((providerId): providerId is string => Boolean(providerId));
};

/**
 * Firebase SDK bootstrap for HellocashflowAdmin.
 *
 * @remarks
 * This module must stay dependency-light. Importing the Redux store here creates a circular dependency:
 * `firebase.ts -> store -> baseApi -> MemoizedAuth -> firebase.ts`, which can fail at runtime with
 * "Cannot access 'auth' before initialization".
 *
 * Store synchronization lives in `src/lib/firebase/authStoreSync.ts`.
 *
 * Config comes from Vite env vars; see `.env.example`.
 *
 * @see src/lib/firebase/MemoizedAuth.ts
 * @see src/lib/firebase/authStoreSync.ts
 */

// See: https://firebase.google.com/docs/web/learn-more#config-object
const config = {
  apiKey: import.meta.env.VITE_FIREBASE_APIKEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGINGSENDERID,
  appId: import.meta.env.VITE_FIREBASE_APPID,
};

const localEmulator = readLocalAdminFirebaseEmulator({
  projectId: config.projectId,
  host: import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST,
  development: import.meta.env.DEV,
  pageHostname:
    typeof window === "undefined" ? undefined : window.location.hostname,
});

const existingApps = getApps();
const hasExistingDefaultApp = existingApps.some(
  (firebaseApp) => firebaseApp.name === "[DEFAULT]",
);

logger.info("Firebase bootstrap config resolved", {
  operation: "firebase_bootstrap_config_resolved",
  authDomain: config.authDomain,
  projectId: config.projectId,
  storageBucket: config.storageBucket,
  appIdTail: redactTail(config.appId, 8),
  messagingSenderIdTail: redactTail(config.messagingSenderId, 4),
  existingAppCount: existingApps.length,
  hasExistingDefaultApp,
  ...resolveBrowserContext(),
});

const app = hasExistingDefaultApp ? getApp() : initializeApp(config);
export const firestore = getFirestore(app);
export const auth = createAdminAuth(app);

// Cloud Functions region is pinned to avoid accidental cross-region latency.
export const functions = getFunctions(app, "australia-southeast1");
connectLocalAdminFirebaseEmulator(
  { auth, firestore, functions },
  localEmulator,
);

export const subbed: Array<(user: User | null) => void> = [];

// Module-level loading flag - THIS is the key to preventing race conditions
let AUTH_IS_LOADING = true;
let lastAuthBridgeUserPresent = Boolean(auth.currentUser);

logger.debug("Firebase auth instance initialized", {
  operation: "firebase_auth_instance_initialized",
  authDomain: config.authDomain,
  appName: app.name,
  existingAppCount: existingApps.length,
  hasExistingDefaultApp,
  currentUserPresent: Boolean(auth.currentUser),
  ...resolveBrowserContext(),
});

const authStateReadyStartedAtMs = Date.now();
logger.info("Firebase auth state readiness wait started", {
  operation: "firebase_auth_state_ready_begin",
  authDomain: config.authDomain,
  currentUserPresent: Boolean(auth.currentUser),
  elapsedMs: 0,
  ...resolveBrowserContext(),
});

void auth
  .authStateReady()
  .then(() => {
    logger.info("Firebase auth state readiness wait completed", {
      operation: "firebase_auth_state_ready_complete",
      authDomain: config.authDomain,
      currentUserPresent: Boolean(auth.currentUser),
      elapsedMs: Date.now() - authStateReadyStartedAtMs,
      ...resolveBrowserContext(),
    });
  })
  .catch((error: unknown) => {
    logger.warn("Firebase auth state readiness wait failed", {
      operation: "firebase_auth_state_ready_error",
      authDomain: config.authDomain,
      errorName: error instanceof Error ? error.name : "unknown",
      errorCode:
        typeof error === "object" &&
        error !== null &&
        "code" in error &&
        typeof (error as { code?: unknown }).code === "string"
          ? (error as { code: string }).code
          : null,
      elapsedMs: Date.now() - authStateReadyStartedAtMs,
      currentUserPresent: Boolean(auth.currentUser),
      ...resolveBrowserContext(),
    });
  });

onAuthStateChanged(auth, (user: User | null) => {
  // First callback means auth state is determined - no more loading!
  const hadUserBefore = lastAuthBridgeUserPresent;
  AUTH_IS_LOADING = false;
  lastAuthBridgeUserPresent = Boolean(user);

  logger.debug("Firebase auth bridge observed auth state change", {
    operation: "firebase_auth_state_changed",
    hadUserBefore,
    hasUserAfter: lastAuthBridgeUserPresent,
    providerIds: getProviderIds(user),
    subscriberCount: subbed.length,
    ...resolveBrowserContext(),
  });

  // Pass through the live Firebase `User` instance. Spreading it into a plain
  // object strips prototype methods like `getIdTokenResult()`, which breaks
  // admin claim checks after successful login.
  subbed.forEach((cb) => cb(user));
});

// Export function to check loading state
export const isAuthLoading = () => AUTH_IS_LOADING;

export const getFirebaseObserverSubscriberCount = () => subbed.length;

export const getFirebaseAuthDebugSnapshot = () => ({
  authDomain: config.authDomain,
  appName: app.name,
  currentUserPresent: Boolean(auth.currentUser),
  isAuthLoading: AUTH_IS_LOADING,
  projectId: config.projectId,
  subscriberCount: subbed.length,
  ...resolveBrowserContext(),
});

/**
 * Subscribe to Firebase auth state changes.
 *
 * @remarks
 * We fan out `onAuthStateChanged` to multiple subscribers to avoid importing the Redux store here.
 * Callers are responsible for unsubscribing.
 */
export const subScribeToAuthStateChange = (
  callback: (user: User | null) => void,
) => {
  subbed.push(callback);
  return () => {
    const callbackIndex = subbed.indexOf(callback);
    if (callbackIndex > -1) {
      subbed.splice(callbackIndex, 1);
    }
  };
};

export const updateUserState = () => {
  subbed.forEach((cb) => cb(auth.currentUser ?? null));
};

let token: undefined | string = undefined;

export const setToken = (newToken?: string) => {
  token = newToken;
};

export const getToken = () => {
  return token;
};
